import React, { Component } from 'react';
import Sortable from 'sortablejs';

class SlideThumbnails extends Component {

    constructor(props) {
        super(props);
        this.listRef = React.createRef();
        this.sortable = null;
        this.state = {
            thumbnailScale: 0,
            contextMenu: null,
        };
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
    }

    componentDidMount() {
        this.initSortable();
        this.updateThumbnailScale();
        this.resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(() => this.updateThumbnailScale());
        });
        if (this.listRef.current) {
            this.resizeObserver.observe(this.listRef.current);
        }
        document.addEventListener('mousedown', this.handleOutsideClick);
    }

    componentDidUpdate(prevProps) {
        if ((prevProps.cues || []).length !== (this.props.cues || []).length) {
            if (this.sortable) {
                this.sortable.destroy();
            }
            this.initSortable();
        }
    }

    componentWillUnmount() {
        if (this.sortable) {
            this.sortable.destroy();
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        document.removeEventListener('mousedown', this.handleOutsideClick);
    }

    handleOutsideClick(e) {
        if (this.state.contextMenu && this.contextMenuRef && !this.contextMenuRef.contains(e.target)) {
            this.setState({ contextMenu: null });
        }
    }

    handleContextMenu = (e, index) => {
        e.preventDefault();
        this.setState({ contextMenu: { index: index, x: e.clientX, y: e.clientY } });
    }

    handleDuplicateSlide = () => {
        const { contextMenu } = this.state;
        if (!contextMenu || !this.props.presentation || !this.props.onPresentationChange) return;
        const updated = JSON.parse(JSON.stringify(this.props.presentation));
        const cues = updated.cues || [];
        const clone = JSON.parse(JSON.stringify(cues[contextMenu.index]));
        if (clone.uuid && clone.uuid.string) {
            clone.uuid = { string: clone.uuid.string + '-copy-' + Date.now() };
        }
        cues.splice(contextMenu.index + 1, 0, clone);
        updated.cues = cues;
        this.setState({ contextMenu: null });
        this.props.onPresentationChange(updated, true);
        if (this.props.onSelectSlide) {
            this.props.onSelectSlide(contextMenu.index + 1);
        }
    }

    handleAddBlankSlide = () => {
        if (!this.props.presentation || !this.props.onPresentationChange) return;
        const updated = JSON.parse(JSON.stringify(this.props.presentation));
        const cues = updated.cues || [];
        const insertAt = (this.props.selectedIndex != null ? this.props.selectedIndex : cues.length - 1) + 1;
        const blankCue = {
            uuid: { string: 'blank-' + Date.now() },
            name: '',
            actions: [],
        };
        cues.splice(insertAt, 0, blankCue);
        updated.cues = cues;
        this.props.onPresentationChange(updated, true);
        if (this.props.onSelectSlide) {
            this.props.onSelectSlide(insertAt);
        }
    }

    handleToggleDisabled = () => {
        const { contextMenu } = this.state;
        if (!contextMenu || !this.props.presentation || !this.props.onPresentationChange) return;
        const updated = JSON.parse(JSON.stringify(this.props.presentation));
        const cue = (updated.cues || [])[contextMenu.index];
        if (!cue) return;
        cue.isEnabled = cue.isEnabled === false ? true : false;
        this.setState({ contextMenu: null });
        this.props.onPresentationChange(updated, true);
    }

    handleDeleteSlide = () => {
        const { contextMenu } = this.state;
        if (!contextMenu || !this.props.presentation || !this.props.onPresentationChange) return;
        const updated = JSON.parse(JSON.stringify(this.props.presentation));
        const cues = updated.cues || [];
        if (cues.length <= 1) {
            this.setState({ contextMenu: null });
            return;
        }
        cues.splice(contextMenu.index, 1);
        updated.cues = cues;
        this.setState({ contextMenu: null });
        this.props.onPresentationChange(updated, true);
        if (this.props.onSelectSlide && contextMenu.index <= this.props.selectedIndex) {
            const newIndex = Math.max(0, this.props.selectedIndex - 1);
            this.props.onSelectSlide(newIndex);
        }
    }

    updateThumbnailScale = () => {
        if (this.listRef.current) {
            // Each item has border-4 (8px total), so subtract that from list width
            const width = this.listRef.current.offsetWidth - 8;
            this.setState({ thumbnailScale: width / 1920 });
        }
    }

    initSortable() {
        if (this.listRef.current) {
            this.sortable = Sortable.create(this.listRef.current, {
                animation: 150,
                onEnd: (evt) => {
                    const { oldIndex, newIndex } = evt;
                    if (oldIndex !== newIndex && this.props.presentation && this.props.onPresentationChange) {
                        const updated = JSON.parse(JSON.stringify(this.props.presentation));
                        const cues = updated.cues || [];
                        const [moved] = cues.splice(oldIndex, 1);
                        cues.splice(newIndex, 0, moved);
                        updated.cues = cues;
                        this.props.onPresentationChange(updated);
                    }
                }
            });
        }
    }

    parseRtfRuns(rtfData, defaults) {
        if (!rtfData) return [];
        try {
            const decoded = atob(rtfData);
            const pardIdx = decoded.lastIndexOf('\\pard');
            if (pardIdx === -1) return [];

            var defs = defaults || {};
            let pos = pardIdx + 5;
            const runs = [];
            let currentText = '';
            let bold = defs.bold || false, italic = defs.italic || false, underline = false, ucCount = 1;
            const stateStack = [];

            function flushRun() {
                if (currentText.length > 0) {
                    runs.push({ text: currentText, bold: bold, italic: italic, underline: underline });
                    currentText = '';
                }
            }

            while (pos < decoded.length) {
                var ch = decoded[pos];
                if (ch === '{') {
                    stateStack.push({ bold: bold, italic: italic, underline: underline, ucCount: ucCount });
                    pos++;
                } else if (ch === '}') {
                    flushRun();
                    if (stateStack.length > 0) {
                        var prev = stateStack.pop();
                        bold = prev.bold; italic = prev.italic;
                        underline = prev.underline; ucCount = prev.ucCount;
                    } else { break; }
                    pos++;
                } else if (ch === '\\') {
                    pos++;
                    if (pos >= decoded.length) break;
                    var next = decoded[pos];
                    if (next === "'") {
                        if (pos + 2 < decoded.length) {
                            var hex = decoded.substring(pos + 1, pos + 3);
                            var charCode = parseInt(hex, 16);
                            if (!isNaN(charCode)) currentText += String.fromCharCode(charCode);
                            pos += 3;
                        } else { pos++; }
                    } else if (next === '\\') { currentText += '\\'; pos++;
                    } else if (next === '{') { currentText += '{'; pos++;
                    } else if (next === '}') { currentText += '}'; pos++;
                    } else if (next === '~') { currentText += '\u00A0'; pos++;
                    } else if (next === '_') { currentText += '\u2011'; pos++;
                    } else if (next === '-') { pos++;
                    } else if (next === '\n' || next === '\r') { pos++;
                    } else {
                        var word = '';
                        while (pos < decoded.length && ((decoded[pos] >= 'a' && decoded[pos] <= 'z') || (decoded[pos] >= 'A' && decoded[pos] <= 'Z'))) {
                            word += decoded[pos]; pos++;
                        }
                        var paramStr = '';
                        if (pos < decoded.length && (decoded[pos] === '-' || (decoded[pos] >= '0' && decoded[pos] <= '9'))) {
                            if (decoded[pos] === '-') { paramStr += '-'; pos++; }
                            while (pos < decoded.length && decoded[pos] >= '0' && decoded[pos] <= '9') { paramStr += decoded[pos]; pos++; }
                        }
                        if (pos < decoded.length && decoded[pos] === ' ') pos++;
                        var param = paramStr.length > 0 ? parseInt(paramStr, 10) : null;

                        if (word === 'b') { flushRun(); bold = param !== 0;
                        } else if (word === 'i') { flushRun(); italic = param !== 0;
                        } else if (word === 'ul') { flushRun(); underline = param !== 0;
                        } else if (word === 'ulnone') { flushRun(); underline = false;
                        } else if (word === 'uc') { ucCount = param !== null ? param : 1;
                        } else if (word === 'u' && param !== null) {
                            var code = param;
                            if (code < 0) code += 65536;
                            if (code === 8232 || code === 8233) { currentText += '\n'; }
                            else { currentText += String.fromCharCode(code); }
                            for (var sk = 0; sk < ucCount; sk++) {
                                if (pos >= decoded.length) break;
                                if (decoded[pos] === '\\' && pos + 1 < decoded.length && decoded[pos + 1] === "'") { pos += 4; }
                                else if (decoded[pos] !== '\\' && decoded[pos] !== '{' && decoded[pos] !== '}') { pos++; }
                                else { break; }
                            }
                        }
                    }
                } else if (ch === '\n' || ch === '\r') { pos++;
                } else { currentText += ch; pos++; }
            }

            flushRun();
            if (runs.length > 0) {
                runs[0].text = runs[0].text.replace(/^\s+/, '');
                runs[runs.length - 1].text = runs[runs.length - 1].text.replace(/\s+$/, '');
            }
            return runs.filter(function(r) { return r.text.length > 0; });
        } catch (e) {
            return [];
        }
    }

    extractTextFromRtf(rtfData) {
        return this.parseRtfRuns(rtfData).map(function(r) { return r.text; }).join('');
    }

    rgbaFromProtoColor(c) {
        if (!c) return null;
        if (!c.red && !c.green && !c.blue && c.alpha === undefined) return null;
        const r = Math.round((c.red || 0) * 255);
        const g = Math.round((c.green || 0) * 255);
        const b = Math.round((c.blue || 0) * 255);
        const a = c.alpha !== undefined ? c.alpha : 1;
        return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
    }

    hasClearAllAction(cue) {
        if (!cue) return false;
        return (cue.actions || []).some(action => action.clear != null || action.clearGroup != null);
    }

    hasTimerAction(cue) {
        if (!cue) return false;
        return (cue.actions || []).some(action => action.timer != null);
    }

    getMediaAction(cue) {
        if (!cue) return null;
        return (cue.actions || []).find(action => action.media != null) || null;
    }

    getMediaFilename(mediaAction) {
        if (!mediaAction || !mediaAction.media || !mediaAction.media.element) return null;
        const url = mediaAction.media.element.url;
        if (!url) return null;
        const path = (url.local && url.local.path) || url.absolute_string || '';
        return path.split('/').pop() || null;
    }

    isVideoFile(filename) {
        if (!filename) return false;
        return /\.(mp4|mov|m4v|avi|wmv|mkv|webm)$/i.test(filename);
    }

    getMediaUrl(filename) {
        const docId = this.props.documentId;
        if (!docId || !filename) return null;
        return '/slides/editor/' + docId + '/media/' + encodeURIComponent(filename);
    }

    getSlideBackgroundColor(cue) {
        if (!cue) return null;
        const actions = cue.actions || [];
        for (const action of actions) {
            const baseSlide = action.slide && action.slide.presentation && action.slide.presentation.baseSlide;
            if (baseSlide && baseSlide.background_color) {
                return this.rgbaFromProtoColor(baseSlide.background_color);
            }
        }
        return null;
    }

    getAllElements(cue) {
        if (!cue) return [];
        const actions = cue.actions || [];
        const allElements = [];
        actions.forEach(action => {
            const baseSlide = action.slide && action.slide.presentation && action.slide.presentation.baseSlide;
            if (!baseSlide || !baseSlide.elements) return;
            baseSlide.elements.forEach(el => allElements.push(el));
        });
        return allElements;
    }

    getElementStyle(element) {
        const el = element.element;
        if (!el) return {};
        const bounds = el.bounds || {};
        const origin = bounds.origin || {};
        const size = bounds.size || {};
        const style = {
            position: 'absolute',
            left: (origin.x || 0) + 'px',
            top: (origin.y || 0) + 'px',
            width: (size.width || 200) + 'px',
            height: (size.height || 100) + 'px',
        };
        if (el.opacity !== undefined) {
            style.opacity = el.opacity;
        }
        if (el.rotation !== undefined && el.rotation !== 0) {
            style.transform = 'rotate(' + el.rotation + 'deg)';
        }
        return style;
    }

    mapAlignment(value) {
        var v = String(value || '');
        if (v.includes('LEFT') || v === '0') return 'left';
        if (v.includes('RIGHT') || v === '1') return 'right';
        if (v.includes('CENTER') || v === '2') return 'center';
        if (v.includes('JUSTIFIED') || v === '3') return 'justify';
        if (v.includes('NATURAL') || v === '4') return 'left';
        return 'left';
    }

    getTextProps(element) {
        const el = element.element;
        if (!el || !el.text) return null;
        const textEl = el.text;
        const attrs = textEl.attributes || {};
        const font = attrs.font || {};
        const paragraphStyle = attrs.paragraphStyle || {};
        const textFill = attrs.textSolidFill || {};

        const defaultBold = font.bold || (font.name && /bold/i.test(font.name)) || false;
        const defaultItalic = font.italic || (font.name && /italic/i.test(font.name)) || false;
        const runs = this.parseRtfRuns(textEl.rtfData, { bold: defaultBold, italic: defaultItalic });
        const color = this.rgbaFromProtoColor(textFill) || '#ffffff';
        const familyName = (font.family || '').trim();
        const faceFallback = (font.name || '').replace(/\s*(bold|italic|oblique|heavy|light|medium|regular|thin|black|semibold|demibold|ultra|condensed)/gi, '').trim();
        const fontFamily = (familyName || faceFallback) ? ((familyName || faceFallback) + ', sans-serif') : 'sans-serif';
        const fontSize = font.size || 48;

        const textAlign = this.mapAlignment(paragraphStyle.alignment);

        let justifyContent = 'center';
        const vAlign = String(textEl.verticalAlignment || '');
        if (vAlign.includes('TOP') || vAlign === '0') justifyContent = 'flex-start';
        else if (vAlign.includes('MIDDLE') || vAlign === '1') justifyContent = 'center';
        else if (vAlign.includes('BOTTOM') || vAlign === '2') justifyContent = 'flex-end';

        return { runs, color, fontFamily, fontSize, textAlign, justifyContent };
    }

    renderSlideElements(cue) {
        const elements = this.getAllElements(cue);
        return elements.map((element, index) => {
            const el = element.element;
            if (!el || el.hidden) return null;

            const style = this.getElementStyle(element);

            if (el.fill && el.fill.color) {
                style.backgroundColor = this.rgbaFromProtoColor(el.fill.color);
            }
            if (el.stroke && el.stroke.width) {
                style.border = el.stroke.width + 'px solid ' + (this.rgbaFromProtoColor(el.stroke.color) || '#ffffff');
            }

            const textProps = el.text ? this.getTextProps(element) : null;

            return (
                <div key={index} style={style}>
                    {textProps && (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: textProps.justifyContent,
                                padding: 8,
                                overflow: 'hidden',
                            }}
                        >
                            <div style={{
                                color: textProps.color,
                                fontFamily: textProps.fontFamily,
                                fontSize: textProps.fontSize + 'px',
                                lineHeight: 1.2,
                                textAlign: textProps.textAlign,
                                wordBreak: 'break-word',
                            }}>
                                {textProps.runs.map(function(run, ri) {
                                    return React.createElement('span', {
                                        key: ri,
                                        style: {
                                            fontWeight: run.bold ? 'bold' : 'normal',
                                            fontStyle: run.italic ? 'italic' : 'normal',
                                            textDecoration: run.underline ? 'underline' : 'none',
                                        }
                                    }, run.text);
                                })}
                            </div>
                        </div>
                    )}
                </div>
            );
        });
    }

    render() {
        const { cues, selectedIndex, onSelectSlide } = this.props;
        const { thumbnailScale } = this.state;
        const scaledWidth = 1920 * thumbnailScale;
        const scaledHeight = 1080 * thumbnailScale;

        const { contextMenu } = this.state;

        return (
            <div className="p-2">
                <div ref={this.listRef}>
                    {(cues || []).map((cue, index) => {
                        const isSelected = index === selectedIndex;
                        const bgColor = this.getSlideBackgroundColor(cue);

                        return (
                            <div
                                key={(cue.uuid && cue.uuid.string) || index}
                                className={"mb-2 rounded-sm cursor-pointer border-4 transition-colors duration-100 " +
                                    (isSelected ? "border-blue-500" : "border-transparent hover:border-gray-300")}
                                onClick={() => onSelectSlide(index)}
                                onContextMenu={(e) => this.handleContextMenu(e, index)}
                            >
                                <div
                                    className="bg-gray-800 rounded-sm relative overflow-hidden"
                                    style={{ height: scaledHeight || 0, opacity: cue.isEnabled === false ? 0.35 : 1 }}
                                >
                                    {thumbnailScale > 0 && (() => {
                                        const mediaAction = this.getMediaAction(cue);
                                        const mediaFilename = this.getMediaFilename(mediaAction);
                                        const mediaUrl = this.getMediaUrl(mediaFilename);
                                        const isVideo = this.isVideoFile(mediaFilename);

                                        return (
                                            <>
                                                {mediaUrl && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        zIndex: 1,
                                                    }}>
                                                        {isVideo ? (
                                                            <video
                                                                src={mediaUrl}
                                                                muted
                                                                preload="metadata"
                                                                style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000' }}
                                                            />
                                                        ) : (
                                                            <img
                                                                src={mediaUrl}
                                                                alt={mediaFilename}
                                                                style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000' }}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                                <div style={{
                                                    width: 1920,
                                                    height: 1080,
                                                    transform: 'scale(' + thumbnailScale + ')',
                                                    transformOrigin: 'top left',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    backgroundColor: bgColor || undefined,
                                                }}>
                                                    {this.renderSlideElements(cue)}
                                                </div>
                                            </>
                                        );
                                    })()}
                                    {(this.hasClearAllAction(cue) || this.hasTimerAction(cue)) && (
                                        <div style={{ position: 'absolute', top: 4, left: 4, display: 'flex', gap: 4, zIndex: 2 }}>
                                            {this.hasClearAllAction(cue) && (
                                                <svg width="18" height="18" viewBox="0 0 18 18">
                                                    <circle cx="9" cy="9" r="8" fill="white" />
                                                    <line x1="5" y1="5" x2="13" y2="13" stroke="black" strokeWidth="2" strokeLinecap="round" />
                                                    <line x1="13" y1="5" x2="5" y2="13" stroke="black" strokeWidth="2" strokeLinecap="round" />
                                                </svg>
                                            )}
                                            {this.hasTimerAction(cue) && (
                                                <svg width="18" height="18" viewBox="0 0 18 18">
                                                    <circle cx="9" cy="9" r="7" fill="white" />
                                                    <circle cx="9" cy="9" r="6" fill="none" stroke="black" strokeWidth="1.5" />
                                                    <line x1="9" y1="5" x2="9" y2="9" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
                                                    <line x1="9" y1="9" x2="12" y2="11" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
                                                </svg>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 text-center mt-1 truncate px-1">
                                    {index + 1}. {cue.name || 'Slide'}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {contextMenu && (
                    <div
                        ref={(el) => { this.contextMenuRef = el; }}
                        className="fixed bg-white shadow-lg rounded-sm border border-gray-200 py-1 z-50"
                        style={{ left: contextMenu.x, top: contextMenu.y }}
                    >
                        <button
                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                            onClick={this.handleDuplicateSlide}
                        >
                            Duplicate Slide
                        </button>
                        <button
                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                            onClick={this.handleToggleDisabled}
                        >
                            {((cues || [])[contextMenu.index] || {}).isEnabled === false ? 'Enable Slide' : 'Disable Slide'}
                        </button>
                        <button
                            className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100"
                            onClick={this.handleDeleteSlide}
                        >
                            Delete Slide
                        </button>
                    </div>
                )}
            </div>
        );
    }
}

export default SlideThumbnails;
